import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  Typography,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Button,
  Space,
  message,
  Modal,
  Divider,
  Tag,
  Alert,
  Segmented,
  Spin,
  Table,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  SaveOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  RollbackOutlined,
  EditOutlined,
  CloseOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useCan, useGetIdentity } from '@refinedev/core';
import dayjs from 'dayjs';
import { adminApi } from '@/services/api';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import type {
  MatchdayDirectorConfigAction,
  MatchdayDirectorConfigChangeEntry,
  MatchdayDirectorConfigFieldChange,
  MatchdayDirectorConfigHistoryResponse,
  MatchdayDirectorConfigPayload,
} from '@/types';
import { GddRangesEditor } from './GddRangesEditor';
import { touchesClientCopiedGdd } from './gddPreview';

const { Title, Text } = Typography;
const { confirm } = Modal;

const HISTORY_PAGE_SIZE = 20;
const COMMENT_MIN = 3;
const COMMENT_MAX = 500;
/** Values longer than this are cut short in change lists; the full value is in a tooltip. */
const VALUE_PREVIEW_MAX = 60;

const ACTION_TAGS: Record<MatchdayDirectorConfigAction, { color: string; label: string }> = {
  update: { color: 'blue', label: 'Update' },
  reset: { color: 'orange', label: 'Reset to defaults' },
  override_removed: { color: 'red', label: 'Override removed' },
};

interface AdminIdentity {
  id: string;
  name?: string;
  email?: string;
}

/** Writes that need a comment before they are sent. */
type PendingAction =
  | {
      kind: 'save';
      /**
       * The form's values: only the sections it renders (no legacy offsets in
       * GDD mode, no `gddRanges` in legacy mode). The backend keeps every
       * section the body leaves out.
       */
      values: Partial<MatchdayDirectorConfigPayload>;
      changes: MatchdayDirectorConfigFieldChange[];
    }
  | { kind: 'reset' }
  | { kind: 'remove' };

type HistoryMode = 'scope' | 'all';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isUnset = (value: unknown): value is null | undefined =>
  value === null || value === undefined;

/**
 * Deep equality the way the backend's config diff has it: null and undefined
 * both mean "not set", numbers compare by value, objects key by key in any
 * order, arrays item by item.
 */
const isSameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (isUnset(a) || isUnset(b)) return isUnset(a) && isUnset(b);
  if (typeof a === 'number' && typeof b === 'number') return Number.isNaN(a) && Number.isNaN(b);
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, i) => isSameValue(item, b[i]))
    );
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].every((key) => isSameValue(a[key], b[key]));
  }
  return false;
};

/**
 * Field-level diff as dot paths, the same shape the history endpoint returns.
 * Walks only the fields the form submits, so a key the server returns but the
 * form does not render (the legacy offsets in GDD mode, `gddRanges` in legacy
 * mode) is never reported as changed. Leaves compare by value (isSameValue),
 * never as JSON text, so key order can't make a false change.
 */
const diffConfig = (
  before: unknown,
  after: unknown,
  path = ''
): MatchdayDirectorConfigFieldChange[] => {
  if (isPlainObject(after) && (isPlainObject(before) || isUnset(before))) {
    const previous = isPlainObject(before) ? before : {};
    return Object.keys(after).flatMap((key) =>
      diffConfig(previous[key], after[key], path ? `${path}.${key}` : key)
    );
  }
  return isSameValue(before, after) ? [] : [{ field: path, from: before, to: after }];
};

const formatValue = (value: unknown): string => {
  // The history endpoint sends null for a field that was not set.
  if (value === undefined || value === null) return '(unset)';
  if (typeof value === 'string') return value;
  return JSON.stringify(value) ?? String(value);
};

/**
 * Leaf messages of the backend's `validationErrors` map. Newer backends send a flat
 * dotted-key map; older ones nest it per field. Both reduce to the same leaves.
 */
const flattenValidationErrors = (errors: unknown): string[] => {
  if (typeof errors === 'string') return errors ? [errors] : [];
  if (!isPlainObject(errors)) return [];
  return Object.values(errors).flatMap(flattenValidationErrors);
};

const errorMessage = (err: unknown, fallback: string): string => {
  const data = (
    err as {
      response?: { data?: { message?: string | string[]; validationErrors?: unknown } };
    }
  ).response?.data;
  // Prefer the per-field reasons (e.g. the comment length). Older backends put
  // only "Unprocessable Entity" in a 422's `message`, so it is the fallback.
  const validation = flattenValidationErrors(data?.validationErrors);
  if (validation.length) return validation.join('; ');
  const msg = data?.message;
  if (Array.isArray(msg)) return msg.join(', ') || fallback;
  return msg || fallback;
};

const ValueText: React.FC<{ value: unknown }> = ({ value }) => {
  const full = formatValue(value);
  if (full.length <= VALUE_PREVIEW_MAX) return <Text code>{full}</Text>;
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{full}</span>}>
      <Text code>{`${full.slice(0, VALUE_PREVIEW_MAX - 1)}…`}</Text>
    </Tooltip>
  );
};

/** "field: old → new" */
const ChangeLine: React.FC<{ change: MatchdayDirectorConfigFieldChange }> = ({ change }) => (
  <div style={{ lineHeight: 1.9 }}>
    <Text strong>{change.field}</Text>: <ValueText value={change.from} /> →{' '}
    <ValueText value={change.to} />
  </div>
);

interface CommentModalProps {
  open: boolean;
  title: string;
  okText: string;
  danger?: boolean;
  identity?: AdminIdentity | null;
  children?: React.ReactNode;
  /** Handles its own errors; the parent closes the modal once the write succeeds. */
  onSubmit: (comment: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * Confirmation for every config write. The comment is required and lands in
 * the change history next to the diff, so the next person can see why.
 */
const CommentModal: React.FC<CommentModalProps> = ({
  open,
  title,
  okText,
  danger,
  identity,
  children,
  onSubmit,
  onCancel,
}) => {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const trimmedLength = comment.trim().length;
  const valid = trimmedLength >= COMMENT_MIN && trimmedLength <= COMMENT_MAX;

  const handleOk = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit(comment.trim());
    } finally {
      setSubmitting(false);
    }
  };

  const who = identity?.name || identity?.email;

  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      okButtonProps={{ danger, disabled: !valid }}
      confirmLoading={submitting}
      cancelButtonProps={{ disabled: submitting }}
      closable={!submitting}
      maskClosable={false}
      onOk={() => void handleOk()}
      onCancel={() => {
        if (!submitting) onCancel();
      }}
      width={640}
    >
      {children}
      <div style={{ marginTop: 16 }}>
        <Text strong>Comment</Text> <Text type="secondary">(required — why is this changing?)</Text>
        <Input.TextArea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={COMMENT_MAX}
          showCount
          rows={3}
          autoFocus
          disabled={submitting}
          placeholder="e.g. Easing difficulty for new players after the D1 retention dip"
          style={{ marginTop: 8 }}
        />
        {trimmedLength > 0 && trimmedLength < COMMENT_MIN && (
          <Text type="danger">Write at least {COMMENT_MIN} characters.</Text>
        )}
      </div>
      {who && (
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            Saved as <Text strong>{who}</Text>
            {identity?.name && identity.email ? ` (${identity.email})` : ''}
          </Text>
        </div>
      )}
    </Modal>
  );
};

/**
 * §2.6 — Matchday-Director configuration editor.
 *
 * Drives the AI difficulty / churn / win-loss tuning constants that the
 * backend's `matchday-director.service.ts` reads on every match flow.
 * GET / PUT / reset endpoints are atomic; in-flight matches finish with
 * whatever profile the previous config produced.
 *
 * Read-only until Edit is pressed. Every write goes through a review modal
 * that shows the diff and requires a comment, and lands in the change history
 * below the form.
 *
 * The AI's run-rate / economy targets have two editors: the GDD ranges while
 * the `matchday_director_gdd_ranges` feature flag is on (GddRangesEditor), the
 * legacy offsets otherwise. A save sends only the sections on screen; the
 * backend keeps the others, so neither mode wipes the other's values.
 */
export const MatchdayDirectorPage: React.FC = () => {
  const [form] = Form.useForm<MatchdayDirectorConfigPayload>();
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  // The values as last loaded from the server: the baseline for the diff and
  // what Cancel restores.
  const [loadedConfig, setLoadedConfig] = useState<MatchdayDirectorConfigPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{
    updatedBy?: string | null;
    updatedAt?: string;
  }>({});

  // Which build's tuning is being edited. Null is the shared config every build
  // inherits — the only thing that existed before per-build config.
  const [scopeConfigVersionId, setScopeConfigVersionId] = useState<string | null>(null);
  // True when the selected version has no override and is showing shared values.
  // Saving in that state forks an override, so the UI has to say so first.
  const [inherited, setInherited] = useState(false);

  // Which AI-range editor to show, from the last successful load: the GDD cards
  // while the `matchday_director_gdd_ranges` flag is on and the server sent the
  // values, the legacy offset cards otherwise (an older backend sends neither).
  const [gddMode, setGddMode] = useState(false);
  // The flag is on but the server sent no `gddRanges` (a backend mismatch).
  const [gddValuesMissing, setGddValuesMissing] = useState(false);

  const [pending, setPending] = useState<PendingAction | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Remounts the modal on every open so the comment box starts empty.
  const [modalKey, setModalKey] = useState(0);

  const [versionLabels, setVersionLabels] = useState<Record<string, string>>({});

  const [historyMode, setHistoryMode] = useState<HistoryMode>('scope');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyReloadKey, setHistoryReloadKey] = useState(0);
  const [history, setHistory] = useState<MatchdayDirectorConfigHistoryResponse>({
    items: [],
    total: 0,
  });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const { data: canEditResult, isLoading: canEditLoading } = useCan({
    resource: 'config',
    action: 'edit',
  });
  const canEdit = canEditResult?.can === true;
  const { data: identity } = useGetIdentity<AdminIdentity>();

  // Scope switches can overlap; only the latest request may write state.
  const configRequestRef = useRef(0);
  const historyRequestRef = useRef(0);

  const fetchConfig = useCallback(async () => {
    const requestId = ++configRequestRef.current;
    setLoading(true);
    try {
      const { data: payload } = await adminApi.getMatchdayDirectorConfig(scopeConfigVersionId);
      if (requestId !== configRequestRef.current) return;
      form.resetFields();
      form.setFieldsValue(payload.config);
      setLoadedConfig(payload.config);
      setLoadError(null);
      setMeta({ updatedBy: payload.updatedBy, updatedAt: payload.updatedAt });
      setInherited(Boolean(payload.inherited));
      const gddFlagOn = payload.gddRangesEnabled === true;
      const hasGddValues = isPlainObject(payload.config.gddRanges);
      setGddMode(gddFlagOn && hasGddValues);
      setGddValuesMissing(gddFlagOn && !hasGddValues);
    } catch (err) {
      if (requestId !== configRequestRef.current) return;
      // Never leave the previous scope's values on screen as the baseline:
      // editing them would save that scope's numbers into this one.
      form.resetFields();
      setLoadedConfig(null);
      setMeta({});
      setLoadError(errorMessage(err, 'Failed to load Matchday-Director config'));
    } finally {
      if (requestId === configRequestRef.current) setLoading(false);
    }
  }, [form, scopeConfigVersionId]);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  const fetchHistory = useCallback(async () => {
    const requestId = ++historyRequestRef.current;
    setHistoryLoading(true);
    try {
      const { data } = await adminApi.getMatchdayDirectorConfigHistory({
        configVersionId: scopeConfigVersionId,
        all: historyMode === 'all',
        limit: HISTORY_PAGE_SIZE,
        offset: (historyPage - 1) * HISTORY_PAGE_SIZE,
      });
      if (requestId !== historyRequestRef.current) return;
      setHistory({ items: data.items ?? [], total: data.total ?? 0 });
      setHistoryError(null);
    } catch (err) {
      if (requestId !== historyRequestRef.current) return;
      setHistory({ items: [], total: 0 });
      setHistoryError(errorMessage(err, 'Failed to load change history'));
    } finally {
      if (requestId === historyRequestRef.current) setHistoryLoading(false);
    }
  }, [scopeConfigVersionId, historyMode, historyPage]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory, historyReloadKey]);

  // Labels for the history's Scope column. Without them it shows the id.
  useEffect(() => {
    let cancelled = false;
    adminApi
      .getConfigVersions()
      .then((res) => {
        if (cancelled) return;
        setVersionLabels(Object.fromEntries((res.data ?? []).map((cv) => [cv.id, cv.version])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /** Newest entries are on page 1, so every write jumps back there. */
  const refreshHistory = () => {
    setHistoryPage(1);
    setHistoryReloadKey((k) => k + 1);
  };

  const scopeLabel = (configVersionId: string | null) =>
    configVersionId ? (versionLabels[configVersionId] ?? configVersionId) : 'Shared';

  const hasUnsavedChanges = () =>
    loadedConfig !== null && diffConfig(loadedConfig, form.getFieldsValue()).length > 0;

  const restoreLoaded = () => {
    form.resetFields();
    if (loadedConfig) form.setFieldsValue(loadedConfig);
  };

  const handleCancelEdit = () => {
    restoreLoaded();
    setEditing(false);
  };

  const handleScopeChange = (next: string | null) => {
    if (next === scopeConfigVersionId) return;
    const apply = () => {
      setEditing(false);
      setScopeConfigVersionId(next);
      setHistoryPage(1);
    };
    if (editing && hasUnsavedChanges()) {
      confirm({
        title: 'Discard unsaved changes?',
        icon: <ExclamationCircleOutlined />,
        content: 'You have edits that have not been saved. Switching config version discards them.',
        okText: 'Discard changes',
        okType: 'danger',
        cancelText: 'Keep editing',
        onOk: apply,
      });
      return;
    }
    apply();
  };

  const openModal = (action: PendingAction) => {
    setPending(action);
    setModalKey((k) => k + 1);
    setModalOpen(true);
  };

  // Form onFinish: validation passed, now show what is about to change.
  const handleReview = (values: Partial<MatchdayDirectorConfigPayload>) => {
    if (!loadedConfig) return;
    const changes = diffConfig(loadedConfig, values);
    if (changes.length === 0) {
      message.info('No changes to save');
      return;
    }
    openModal({ kind: 'save', values, changes });
  };

  const submitPending = async (comment: string) => {
    if (!pending) return;
    try {
      if (pending.kind === 'save') {
        await adminApi.updateMatchdayDirectorConfig(pending.values, comment, scopeConfigVersionId);
        message.success(
          !scopeConfigVersionId
            ? 'Matchday-Director config saved'
            : inherited
              ? 'Override created for this config version'
              : 'Saved for this config version only'
        );
        setEditing(false);
      } else if (pending.kind === 'reset') {
        await adminApi.resetMatchdayDirectorConfig(comment, scopeConfigVersionId);
        message.success('Restored to defaults');
      } else {
        if (!scopeConfigVersionId) return;
        await adminApi.deleteMatchdayDirectorOverride(scopeConfigVersionId, comment);
        message.success('Override removed — these builds inherit the shared config again');
      }
    } catch (err) {
      const fallback =
        pending.kind === 'save'
          ? 'Failed to save config'
          : pending.kind === 'reset'
            ? 'Failed to reset'
            : 'Failed to remove override';
      message.error(errorMessage(err, fallback));
      return;
    }
    setModalOpen(false);
    refreshHistory();
    await fetchConfig();
  };

  const currentScopeText = scopeConfigVersionId
    ? `Config version ${scopeLabel(scopeConfigVersionId)} only`
    : 'All builds (shared)';

  const flagWhere = 'Switch it in Configuration → Feature Flags; this page reads it when it loads.';
  const modeTooltip = gddMode
    ? `The matchday_director_gdd_ranges feature flag is on: AI targets come from the GDD ranges below. ${flagWhere}`
    : gddValuesMissing
      ? `The matchday_director_gdd_ranges feature flag is on, but the server sent no GDD values, so the legacy editor is shown. ${flagWhere}`
      : `The matchday_director_gdd_ranges feature flag is off: AI targets use the legacy offsets. ${flagWhere}`;

  const renderModalBody = () => {
    if (!pending) return null;
    if (pending.kind === 'save') {
      const forking = Boolean(scopeConfigVersionId && inherited);
      const clientCopy = touchesClientCopiedGdd(pending.changes.map((change) => change.field));
      return (
        <>
          {clientCopy && (
            <Alert
              type="warning"
              showIcon
              message="Tell the client team before saving"
              description="Some of these values are copied in the Unity client's fallback (bands, usable-average window, range limits, default averages). Saving here without updating the client makes the two differ."
              style={{ marginBottom: 16 }}
            />
          )}
          {forking && (
            <Alert
              type="warning"
              showIcon
              message="This creates an override for this config version"
              description="These builds currently inherit the shared config. Saving gives them their own copy, which will no longer pick up later edits to the shared config. Other builds are unaffected."
              style={{ marginBottom: 16 }}
            />
          )}
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary">
              Applies to: <Text strong>{currentScopeText}</Text> · {pending.changes.length}{' '}
              {pending.changes.length === 1 ? 'field' : 'fields'} changed
            </Text>
          </div>
          <div
            style={{
              maxHeight: 280,
              overflowY: 'auto',
              padding: '8px 12px',
              border: '1px solid rgba(5, 5, 5, 0.06)',
              borderRadius: 6,
            }}
          >
            {pending.changes.map((change) => (
              <ChangeLine key={change.field} change={change} />
            ))}
          </div>
        </>
      );
    }
    if (pending.kind === 'reset') {
      return (
        <Text>
          {scopeConfigVersionId
            ? 'This restores every Matchday-Director value of this config version, both the GDD ranges and the legacy ones, to the in-code defaults. Other builds are unaffected. In-flight matches keep their profile.'
            : 'This restores every Matchday-Director value, both the GDD ranges and the legacy ones, to the in-code defaults. In-flight matches keep their profile; new matches will use the restored values.'}
        </Text>
      );
    }
    return (
      <Text>
        Builds on this config version go back to the shared config. The override and its values are
        deleted.
      </Text>
    );
  };

  const modalTitle =
    pending?.kind === 'reset'
      ? scopeConfigVersionId
        ? 'Restore defaults for this config version?'
        : 'Restore Matchday-Director defaults?'
      : pending?.kind === 'remove'
        ? 'Remove this override?'
        : 'Review changes';
  const modalOkText =
    pending?.kind === 'reset'
      ? 'Restore defaults'
      : pending?.kind === 'remove'
        ? 'Remove override'
        : scopeConfigVersionId && inherited
          ? 'Create override'
          : 'Save changes';

  const actionButtons = editing ? (
    <>
      <Button type="primary" icon={<SaveOutlined />} onClick={() => form.submit()}>
        Save changes
      </Button>
      <Button icon={<CloseOutlined />} onClick={handleCancelEdit}>
        Cancel
      </Button>
    </>
  ) : canEdit ? (
    <>
      <Button
        type="primary"
        icon={<EditOutlined />}
        onClick={() => setEditing(true)}
        disabled={loading || !loadedConfig}
      >
        Edit
      </Button>
      <Button
        danger
        icon={<ReloadOutlined />}
        onClick={() => openModal({ kind: 'reset' })}
        disabled={loading}
      >
        Restore defaults
      </Button>
    </>
  ) : null;

  const historyColumns: ColumnsType<MatchdayDirectorConfigChangeEntry> = [
    {
      title: 'When',
      dataIndex: 'changedAt',
      key: 'changedAt',
      width: 170,
      render: (changedAt: string) => dayjs(changedAt).format('D MMM YYYY, HH:mm'),
    },
    {
      title: 'Who',
      key: 'who',
      width: 180,
      render: (_, entry) =>
        entry.adminEmail ? (
          <Tooltip title={entry.adminEmail}>
            <span>{entry.adminName}</span>
          </Tooltip>
        ) : (
          entry.adminName
        ),
    },
    {
      title: 'Scope',
      key: 'scope',
      width: 150,
      render: (_, entry) =>
        entry.configVersionId ? (
          <Tooltip title={entry.configVersionId}>
            <Tag color="purple">{scopeLabel(entry.configVersionId)}</Tag>
          </Tooltip>
        ) : (
          <Tag>Shared</Tag>
        ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 150,
      render: (action: MatchdayDirectorConfigAction) => {
        const tag = ACTION_TAGS[action] ?? { color: 'default', label: action };
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: 'Changes',
      key: 'changes',
      render: (_, entry) =>
        entry.changes?.length ? (
          entry.changes.map((change) => <ChangeLine key={change.field} change={change} />)
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      width: 260,
      render: (comment: string | null) =>
        comment ? (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{comment}</Text>
        ) : (
          <Tooltip title="Recorded before comments were required">
            <Text type="secondary">—</Text>
          </Tooltip>
        ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              Matchday-Director Config
            </Title>
            {loadedConfig && (
              <Tooltip title={modeTooltip}>
                <Tag color={gddMode ? 'green' : 'default'}>
                  {gddMode ? 'GDD ranges: ON' : 'Legacy ranges'}
                </Tag>
              </Tooltip>
            )}
          </div>
          <Text type="secondary">
            Tunes AI difficulty / churn / win-loss factors. Saving propagates immediately to new
            matches; in-flight matches keep their cached profile. See{' '}
            <Tag color="blue">RETENTION_AND_MATCH_ANALYTICS §2.6</Tag>.
          </Text>
        </div>
      </div>

      <Card size="small" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Space wrap align="center">
            <Text strong>Config for:</Text>
            <ConfigVersionSelect
              value={scopeConfigVersionId}
              onChange={handleScopeChange}
              sharedLabel="All builds (shared)"
            />
            {scopeConfigVersionId ? (
              !loadError && (
                <Tag color={inherited ? 'default' : 'purple'}>
                  {inherited ? 'Inheriting shared' : 'Override'}
                </Tag>
              )
            ) : (
              <Text type="secondary">Changes here affect every build.</Text>
            )}
            {canEdit && scopeConfigVersionId && !inherited && !loadError && (
              <Button
                size="small"
                danger
                icon={<RollbackOutlined />}
                onClick={() => openModal({ kind: 'remove' })}
                disabled={editing || loading}
              >
                Remove override
              </Button>
            )}
          </Space>
          <Space wrap align="center">
            {editing && <Tag color="gold">Editing</Tag>}
            {!editing && !canEdit && !canEditLoading && (
              <Tooltip title="You need config edit permission to change these values.">
                <Tag>Read-only</Tag>
              </Tooltip>
            )}
            {actionButtons}
          </Space>
        </div>
      </Card>

      {loadError && (
        <Alert
          type="error"
          showIcon
          message="Could not load this config"
          description={loadError}
          action={
            <Button size="small" onClick={() => void fetchConfig()} loading={loading}>
              Retry
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {scopeConfigVersionId && inherited && !loadError && (
        <Alert
          type="warning"
          showIcon
          message="These builds inherit the shared config"
          description="The values below are the shared ones. Saving creates an override for this config version, which will stop picking up later edits to the shared config."
          style={{ marginBottom: 16 }}
        />
      )}

      {meta.updatedAt && (
        <Alert
          type="info"
          showIcon
          message={
            <Text>
              Last updated <strong>{new Date(meta.updatedAt).toLocaleString()}</strong>
              {meta.updatedBy ? ` by ${meta.updatedBy}` : ''}
            </Text>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleReview} disabled={!editing}>
          {/* Neither AI-range editor until the first load says which one applies, so a
              GDD page never flashes the legacy cards first. */}
          {loadedConfig !== null &&
            (gddMode ? (
              // The legacy offset cards are not rendered, so they are neither diffed nor
              // sent; the server keeps their saved values.
              <GddRangesEditor />
            ) : (
              <>
                <Alert
                  type={gddValuesMissing ? 'warning' : 'info'}
                  showIcon
                  message={
                    gddValuesMissing
                      ? 'The matchday_director_gdd_ranges feature flag is on, but the server sent no GDD values, so the legacy editor is shown.'
                      : 'Legacy AI ranges (offsets). When the matchday_director_gdd_ranges feature flag is on, this page shows the GDD editor instead.'
                  }
                  style={{ marginBottom: 16 }}
                />

                <Card title="Run-rate range" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name={['runRateRange', 'baseMin']}
                        label="Base min"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} step={0.1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name={['runRateRange', 'baseMax']}
                        label="Base max"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} step={0.1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name={['runRateRange', 'adjustmentFactor']}
                        label="Adjustment factor"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} step={0.05} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Text type="secondary">
                    Range applied to opponent run rate, shifted by (difficultyFactor - 1) ×
                    adjustmentFactor.
                  </Text>
                </Card>

                <Card title="Economy range" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name={['economyRange', 'baseMin']}
                        label="Base min"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} step={0.1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name={['economyRange', 'baseMax']}
                        label="Base max"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} step={0.1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name={['economyRange', 'adjustmentFactor']}
                        label="Adjustment factor"
                        rules={[{ required: true }]}
                      >
                        <InputNumber style={{ width: '100%' }} step={0.05} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Text type="secondary">
                    Shifted by (1 - difficultyFactor) × adjustmentFactor (inverse of run-rate).
                  </Text>
                </Card>
              </>
            ))}

          <Card title="Team-rating range" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['teamRatingRange', 'baseMin']}
                  label="Base min"
                  rules={[{ required: true }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['teamRatingRange', 'baseMax']}
                  label="Base max"
                  rules={[{ required: true }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name={['teamRatingRange', 'adjustmentFactor']}
                  label="Adjustment factor"
                  rules={[{ required: true }]}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Text type="secondary">
              Integer rounding applied to (difficultyFactor - 1) × adjustmentFactor.
            </Text>
          </Card>

          <Divider />

          <Card title="Win/loss factor" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={['winLossFactor', 'defaultWinRate']}
                  label="Default winRate fallback"
                  rules={[{ required: true }]}
                >
                  <InputNumber style={{ width: '100%' }} step={0.05} min={0} max={1} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={['winLossFactor', 'scaleMultiplier']}
                  label="Scale multiplier"
                  rules={[{ required: true }]}
                >
                  <InputNumber style={{ width: '100%' }} step={0.1} min={0} />
                </Form.Item>
              </Col>
            </Row>
            <Text type="secondary">
              wLF = max(0, winRate × scaleMultiplier). At 50% wins with default 2.0 multiplier → wLF
              = 1.0 (medium).
            </Text>
          </Card>

          <Card title="Churn factor" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={['churnFactor', 'baseFactor']}
                  label="Base factor"
                  rules={[{ required: true }]}
                >
                  <InputNumber style={{ width: '100%' }} step={0.05} min={0} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={['churnFactor', 'dayThreshold']}
                  label="Day threshold"
                  rules={[{ required: true }]}
                >
                  <InputNumber style={{ width: '100%' }} min={1} />
                </Form.Item>
              </Col>
            </Row>
            <Text type="secondary">
              cF = baseFactor × min(1, daysSinceLastMatch / dayThreshold).
            </Text>
          </Card>

          <Card title="Churn weight" style={{ marginBottom: 16 }}>
            <Form.Item
              name={['churnWeight', 'divisor']}
              label="Divisor (denominator of churnRiskScore / divisor)"
              rules={[{ required: true }]}
            >
              <InputNumber style={{ width: '100%' }} min={1} />
            </Form.Item>
          </Card>

          <Card title="Difficulty factor" style={{ marginBottom: 16 }}>
            <Form.Item
              name={['difficultyFactor', 'baseDifficulty']}
              label="Base difficulty"
              rules={[{ required: true }]}
            >
              <InputNumber style={{ width: '100%' }} step={0.1} min={0} />
            </Form.Item>
            {gddMode ? (
              <Text type="secondary">
                dF = baseDifficulty × wLF × (1 - cF × cW). With GDD ranges on, the tier (and the
                difficulty sent to the game) comes from the Difficulty tiers thresholds above.
              </Text>
            ) : (
              <Text type="secondary">
                dF = baseDifficulty × wLF × (1 - cF × cW). Mapped to game mode via
                getDifficultyLevel (≥2 = very_hard, ≥1.2 = hard, ≥0.9 = medium, ≥0.6 = easy, &lt;0.6
                = very_easy).
              </Text>
            )}
          </Card>
        </Form>
      </Spin>

      {editing && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap>{actionButtons}</Space>
        </Card>
      )}

      <Card
        title="Change history"
        extra={
          <Space wrap>
            <Segmented
              options={[
                { label: 'This scope', value: 'scope' },
                { label: 'All scopes', value: 'all' },
              ]}
              value={historyMode}
              onChange={(value) => {
                setHistoryMode(value as HistoryMode);
                setHistoryPage(1);
              }}
            />
            <Tooltip title="Refresh">
              <Button
                icon={<SyncOutlined />}
                onClick={() => setHistoryReloadKey((k) => k + 1)}
                loading={historyLoading}
              />
            </Tooltip>
          </Space>
        }
      >
        {historyError && (
          <Alert type="error" showIcon message={historyError} style={{ marginBottom: 16 }} />
        )}
        <Table<MatchdayDirectorConfigChangeEntry>
          rowKey="id"
          size="small"
          columns={historyColumns}
          dataSource={history.items}
          loading={historyLoading}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: historyError ? 'Change history unavailable.' : 'No changes recorded yet.',
          }}
          pagination={{
            current: historyPage,
            pageSize: HISTORY_PAGE_SIZE,
            total: history.total,
            showSizeChanger: false,
            showTotal: (total) => `${total} ${total === 1 ? 'change' : 'changes'}`,
            onChange: setHistoryPage,
          }}
        />
      </Card>

      <CommentModal
        key={modalKey}
        open={modalOpen}
        title={modalTitle}
        okText={modalOkText}
        danger={pending?.kind === 'reset' || pending?.kind === 'remove'}
        identity={identity}
        onSubmit={submitPending}
        onCancel={() => setModalOpen(false)}
      >
        {renderModalBody()}
      </CommentModal>
    </div>
  );
};

export default MatchdayDirectorPage;
